import React from "react";
import { useAccount, useBalance } from "wagmi";
import { formatUnits } from "viem";
import { SUPPORTED_CHAINS } from "@/lib/wallet";
import { Wallet } from "lucide-react";

function ChainRow({ chain }) {
  const { address } = useAccount();
  const { data } = useBalance({
    address,
    chainId: chain.id,
    query: { enabled: !!address && !chain.isSolana, refetchInterval: 30_000 },
  });
  const amount = data?.value != null ? Number(formatUnits(data.value, data.decimals)) : null;
  const usd = amount != null && data?.symbol ? amount * (approxPrice[data.symbol.toUpperCase()] || 0) : null;

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-[var(--border)] last:border-0">
      <img src={chain.icon} alt="" className="w-7 h-7 rounded-full" onError={(e) => (e.currentTarget.style.display = "none")} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-[var(--text)] font-medium">{chain.name}</div>
        <div className="text-[11px] text-[var(--text-muted)] font-mono">{chain.symbol}</div>
      </div>
      <div className="text-right">
        <div className="num-sm text-[var(--text)]">
          {chain.isSolana ? "—" : amount == null ? (address ? "…" : "—") : amount < 0.0001 ? "<0.0001" : amount.toFixed(4)}
        </div>
        {usd != null && usd > 0 && (
          <div className="text-[10.5px] text-[var(--text-muted)] font-mono">${usd.toFixed(2)}</div>
        )}
      </div>
    </div>
  );
}

// Rough native asset prices for quick USD estimate; live pricing lives in the market strip.
const approxPrice = { ETH: 2500, BNB: 650, MATIC: 0.75, ARB: 0.9, OP: 2, AVAX: 30, BASE: 0, SOL: 190 };

export default function PortfolioPanel() {
  const { address, isConnected } = useAccount();

  if (!isConnected) {
    return (
      <div className="px-4 py-10 text-center">
        <div className="mx-auto w-10 h-10 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] flex items-center justify-center mb-3">
          <Wallet size={16} className="text-[var(--text-muted)]" />
        </div>
        <div className="text-[13px] text-[var(--text)] font-medium">Portfolio</div>
        <div className="text-[12px] text-[var(--text-muted)] mt-1 max-w-[240px] mx-auto">
          Connect a wallet to see your native balances across all 8 supported networks.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider">Native balances</div>
        <div className="text-[10.5px] text-[var(--text-muted)] font-mono">
          {address?.slice(0, 6)}…{address?.slice(-4)}
        </div>
      </div>
      <div>
        {SUPPORTED_CHAINS.map((c) => (
          <ChainRow key={c.key} chain={c} />
        ))}
      </div>
    </div>
  );
}
